import { useGridSettingsSelect } from "./index";

// --- Anomaly sensitivity ---------------------------------------------------

export const DEFAULT_ANOMALY_SENSITIVITY_LEVEL = 1;
export const ANOMALY_SENSITIVITY_MIN = 0.1;
export const ANOMALY_SENSITIVITY_STEP = 0.5;

/** Trim floating point drift (e.g. 0.1 + 0.5 -> 0.6000000000000001). */
const roundFloat = (value: number) => Number.parseFloat(value.toFixed(10));

/** Clamp a sensitivity value to the allowed minimum, keeping typed precision. */
export function clampAnomalySensitivity(value: number, fallback = DEFAULT_ANOMALY_SENSITIVITY_LEVEL): number {
  if (Number.isNaN(value)) return fallback;
  return value < ANOMALY_SENSITIVITY_MIN ? ANOMALY_SENSITIVITY_MIN : roundFloat(value);
}

export function incrementAnomalySensitivity(value: number): number {
  // Stepping up from the minimum (0.1) lands on the first regular step (0.5)
  // rather than 0.6, mirroring the decrement snap back down to 0.1.
  if (value <= ANOMALY_SENSITIVITY_MIN) return ANOMALY_SENSITIVITY_STEP;
  return clampAnomalySensitivity(value + ANOMALY_SENSITIVITY_STEP);
}

export function decrementAnomalySensitivity(value: number): number {
  // Anything at or below a single step lands on the minimum rather than
  // stepping below it (spec: "if value is set to <=0.5, decrement sets 0.1").
  if (value <= ANOMALY_SENSITIVITY_STEP) return ANOMALY_SENSITIVITY_MIN;
  return clampAnomalySensitivity(value - ANOMALY_SENSITIVITY_STEP);
}

// --- Range summary ---------------------------------------------------------

export type RangeSummaryCalc = "average" | "sum";

/**
 * DB column default is 2. Existing new-webapp users are backfilled to 4 by the
 * migration; the UI simply reflects whatever is stored (falling back to 2).
 */
export const DEFAULT_RANGE_SUMMARY_DP = 2;
export const RANGE_SUMMARY_DP_MIN = 0;
export const RANGE_SUMMARY_DP_MAX = 5;

export const RANGE_SUMMARY_DP_OPTIONS: { value: string; label: string }[] = [0, 1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  label: String(n),
}));

export function clampRangeSummaryDp(value: number, fallback = DEFAULT_RANGE_SUMMARY_DP): number {
  if (Number.isNaN(value)) return fallback;
  const rounded = Math.round(value);
  return Math.min(RANGE_SUMMARY_DP_MAX, Math.max(RANGE_SUMMARY_DP_MIN, rounded));
}

export const DEFAULT_RANGE_SUMMARY_CALC: RangeSummaryCalc = "average";

export const RANGE_SUMMARY_CALC_OPTIONS: { value: RangeSummaryCalc; label: string }[] = [
  { value: "average", label: "Average" },
  { value: "sum", label: "Sum" },
];

export function parseRangeSummaryCalc(
  value: string | null | undefined,
  fallback: RangeSummaryCalc = DEFAULT_RANGE_SUMMARY_CALC,
): RangeSummaryCalc {
  if (value === "average" || value === "sum") return value;
  return fallback;
}

export const useAnomalySensitivityLevel = () =>
  useGridSettingsSelect((settings) => settings?.anomaly_sensitivity_level ?? DEFAULT_ANOMALY_SENSITIVITY_LEVEL);

export const useRangeSummaryDp = () =>
  useGridSettingsSelect((settings) => settings?.range_summary_dp ?? DEFAULT_RANGE_SUMMARY_DP);

export const useRangeSummaryCalc = () =>
  useGridSettingsSelect((settings) => parseRangeSummaryCalc(settings?.range_summary_calc, DEFAULT_RANGE_SUMMARY_CALC));
